import { useQuery } from "@tanstack/react-query";
import { useId, useState } from "react";

import Combobox from "@/components/Combobox";
import { SelectType } from "@/components/Combobox/types";
import { CountryFlagWithLabel } from "@/components/CountryFlag";
import Icon from "@/components/Icon/Icon";
import { Loader } from "@/components/Loading/Loader";
import { Text } from "@/components/Typography/Text";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import type { ApiResponseBody } from "@/lib/api/api-type-helper";
import { apiClient } from "@/lib/api/client";
import { getDataOrThrowApiError } from "@/lib/api/error";
import { getPath } from "@/lib/api/path";
import { cn } from "@/lib/cn";
import { FIVE_MINUTES } from "@/lib/query";

import {
  CFormItemBase,
  type CFormItemProps,
  buildCFormItem,
} from "../CFormItem";

export interface Country {
  id?: string;
  name?: string;
  flagUrl?: string;
  code?: string;
}

// API関連の型と関数をローカルで定義
type GetCountriesResponse = ApiResponseBody<"/v2/division/countries", "get">;

// 国データ取得API関数
const fetchCountries = async (): Promise<GetCountriesResponse> => {
  const res = await apiClient.GET("/v2/division/countries");
  return getDataOrThrowApiError(res);
};

// API レスポンスの国データの型
type ApiCountry = GetCountriesResponse["countries"][0];

// Combobox オプションの型
type CountryOption = {
  value: string;
  label: string;
  code: string;
  name: string;
};

type Props = CFormItemProps & {
  value?: Country | null;
  onChange?: (value?: Country | null) => void;
  /** 空の場合の表示テキスト */
  placeholder?: string;
  /** 空の選択を許可するか */
  allowEmpty?: boolean;
  /** 入力フィールドの幅 */
  inputWidth?: number | string;
};

/**
 * 国一覧を取得するReact Queryフック
 * CFormCountryItem専用のローカルフック
 */
function useCountries() {
  return useQuery({
    queryKey: [getPath("/v2/division/countries")] as const,
    queryFn: fetchCountries,
    staleTime: FIVE_MINUTES,
  });
}

/**
 * Card-based Form / CountryItem
 * 国情報（フラグ、国名、国コード）を表示するフォームアイテム
 */
export const CFormCountryItem = buildCFormItem((props: Props) => {
  const {
    value,
    placeholder = "-",
    onChange,
    allowEmpty = false,
    inputWidth,
    readonly,
    ...restProps
  } = props;
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const popoverId = useId();

  // 国データを取得
  const { data: countriesData, isLoading, error } = useCountries();

  // valueのcodeからnameを解決する関数
  const resolveCountryName = (country: Country | null): Country | null => {
    if (!country || !country.code) return country;

    // 既にnameがある場合はそのまま返す
    if (country.name) return country;

    // nameが空の場合、countriesDataから名前を解決
    if (countriesData?.countries) {
      const foundCountry = countriesData.countries.find(
        (c: ApiCountry) => c.code === country.code,
      );
      if (foundCountry) {
        return {
          ...country,
          name: foundCountry.name,
        };
      }
    }

    return country;
  };

  // 解決済みの値
  const resolvedValue = resolveCountryName(value ?? null);

  const renderCountryDisplay = (country: Country | null) => {
    if (!country || !country.name) {
      return (
        <Text variant="p" className="text-neutral-muted-on-fill">
          {placeholder}
        </Text>
      );
    }

    return (
      <CountryFlagWithLabel
        countryCode={country.code}
        label={
          <Text
            variant="labelSemiLarge"
            className="text-neutral-on-fill truncate"
          >
            {country.name}
            {country.code && ` (${country.code})`}
          </Text>
        }
        className="min-w-0"
      />
    );
  };

  const renderCountryDisplayCompact = (country: Country | null) => {
    if (!country || !country.name) {
      return placeholder;
    }

    return `${country.name}${country.code ? ` (${country.code})` : ""}`;
  };

  const renderCountrySelector = () => {
    // ローディング中の表示
    if (isLoading) {
      return (
        <Button
          variant="outline"
          disabled
          className={cn("justify-start w-full", "bg-neutral-fill")}
        >
          <Loader size="small" className="mr-2" />
          <Text variant="p" className="text-muted-foreground">
            国データを読み込み中...
          </Text>
        </Button>
      );
    }

    // エラー時の表示
    if (error || !countriesData) {
      return (
        <Button
          variant="outline"
          disabled
          className={cn(
            "justify-start w-full text-destructive border-destructive",
            "bg-neutral-fill",
          )}
        >
          <Text variant="p" className="text-destructive">
            国データの読み込みに失敗しました
          </Text>
        </Button>
      );
    }

    const countries = countriesData.countries || [];
    const comboboxOptions = countries.map((country: ApiCountry) => {
      return {
        value: country.code,
        label: `${country.name} (${country.code})`,
        code: country.code,
        name: country.name,
      };
    });

    const renderCountryOption = ({ option }: { option: CountryOption }) => (
      <CountryFlagWithLabel
        countryCode={option.code}
        label={
          <Text variant="label" className="truncate">
            {option.name}
            {option.code && ` (${option.code})`}
          </Text>
        }
        className="w-full gap-3"
        labelClassName="flex-1 min-w-0"
      />
    );

    return (
      <Popover
        open={isOpen}
        onOpenChange={(open) => {
          setIsOpen(open);
          if (!open) {
            setSearchText("");
          }
        }}
      >
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={isOpen}
            aria-controls={popoverId}
            className={cn(
              "justify-between overflow-hidden text-ellipsis",
              "bg-neutral-fill hover:bg-neutral-fill-hovered",
              !value && "text-muted-foreground",
              inputWidth ? "" : "w-full",
            )}
            style={
              inputWidth
                ? {
                    width:
                      typeof inputWidth === "number"
                        ? `${inputWidth}px`
                        : inputWidth,
                  }
                : undefined
            }
          >
            {resolvedValue ? (
              <CountryFlagWithLabel
                countryCode={resolvedValue.code}
                label={renderCountryDisplayCompact(resolvedValue)}
                className="truncate text-left min-w-0"
                labelClassName="truncate"
              />
            ) : (
              <span className={cn("truncate text-left text-muted-foreground")}>
                {placeholder}
              </span>
            )}
            <Icon
              iconName="ChevronDownIcon"
              className="ml-2 h-4 w-4 shrink-0 opacity-50"
            />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          id={popoverId}
          className={cn("p-0", inputWidth ? "" : "w-full")}
          align="start"
          style={
            inputWidth
              ? {
                  width:
                    typeof inputWidth === "number"
                      ? `${inputWidth}px`
                      : inputWidth,
                }
              : undefined
          }
        >
          <Combobox
            type={SelectType.single}
            value={resolvedValue?.code || null}
            onValueChange={(selectedValue) => {
              if (selectedValue) {
                const selectedCountry = countries.find(
                  (country: ApiCountry) => country.code === selectedValue,
                );
                if (selectedCountry) {
                  onChange?.({
                    id: selectedCountry.code,
                    name: selectedCountry.name,
                    code: selectedCountry.code,
                  });
                }
              } else if (allowEmpty) {
                onChange?.(null);
              }
              setSearchText("");
              setIsOpen(false);
            }}
            options={comboboxOptions}
            search={searchText}
            onSearchChange={setSearchText}
            searchPlaceholder="国名またはコードで検索 (例: 日本, JP, ID)"
            noResultsMessage="該当する国が見つかりません"
            hideSearch={false}
            renderOption={renderCountryOption}
          />
        </PopoverContent>
      </Popover>
    );
  };

  return (
    <CFormItemBase
      {...restProps}
      readComponent={renderCountryDisplay(resolvedValue ?? null)}
      writeComponent={
        readonly
          ? renderCountryDisplay(resolvedValue ?? null)
          : renderCountrySelector()
      }
    />
  );
});
